import type { FontOption } from "@v2/utils/font";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import {
  selectDraftElementFontFamilies,
  selectProject,
  selectSrcDocFonts,
} from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";
import { storeRouter } from "@v1/trpc/routes/store";
import { useUploadedFontOptions } from "@v2/features/settings/shop/hooks/useUploadedFontOptions";
import { GOOGLE_FONT_OPTIONS } from "@v2/utils/font";
import { shallowEqual } from "react-redux";
import { filterNulls } from "replo-utils/lib/array";

export const usePageFontOptions = (): FontOption[] => {
  const pageFonts = useEditorSelector(
    selectDraftElementFontFamilies,
    shallowEqual,
  );
  const pageFontOptions = filterNulls(Array.from(pageFonts)).map(
    (fontFamily) => {
      return {
        value: fontFamily,
        label: fontFamily,
        isDisabled: false,
        isDefaultActive: false,
        isSelectable: true,
      };
    },
  );
  return filterNulls([
    ...pageFontOptions
      .map((option) => {
        return {
          ...option,
          isSelectable: true,
          isDefaultActive: false,
          endEnhancer: null,
          groupTitle: "From Page",
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label)),
  ]);
};

export const useShopifyFontOptions = () => {
  const project = useEditorSelector(selectProject);
  const { data: uploadedFonts } = storeRouter.getFonts.useQuery(
    project?.id ? { storeId: project?.id } : skipToken,
  );

  const srcDocFonts = useEditorSelector(selectSrcDocFonts);

  const uploadedFontNames = uploadedFonts?.map((font) => font.name) ?? [];

  const nameToDisplayName: Record<string, string | undefined> = uploadedFonts
    ? uploadedFonts.reduce(
        (acc, { name, displayName }) => ({
          ...acc,
          [name]: displayName,
        }),
        {},
      )
    : {};

  const themeFontNames = React.useMemo(() => {
    const srcDocFontFamilies = srcDocFonts.map((font) => font.family);
    return filterNulls(srcDocFontFamilies);
  }, [srcDocFonts]);

  const combinedFontNames = [
    ...new Set(uploadedFontNames.concat(themeFontNames)),
  ];

  const formattedFonts = combinedFontNames
    ?.map((fontName) => {
      return {
        label: nameToDisplayName[fontName] ?? fontName,
        isDefaultActive: false,
        isDisabled: false,
        value: fontName ?? null,
        isSelectable: true,
        groupTitle: "From Shopify",
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  return {
    fontOptions: formattedFonts,
    fallbacks: ["sans-serif"],
    nameToDisplayName,
  };
};

export const useAvailableFontFamilies = () => {
  const { fontOptions: shopifyFontOptions, nameToDisplayName } =
    useShopifyFontOptions();
  const { fontOptions: uploadedFontOptions } = useUploadedFontOptions();

  return React.useMemo(() => {
    const allOptions = [
      ...shopifyFontOptions,
      ...uploadedFontOptions,
      ...GOOGLE_FONT_OPTIONS,
    ];

    return Array.from(
      new Set(filterNulls(allOptions.map((option) => option.value))),
    ).map((value) => ({
      name: value,
      displayName: nameToDisplayName[value],
    }));
  }, [shopifyFontOptions, nameToDisplayName, uploadedFontOptions]);
};
